import { useLayoutEffect, useRef } from "react";
import { CalendarReconnectBanner } from "./crm/CalendarReconnectBanner";
import { ReadOnlyModeBanner } from "./crm/ReadOnlyModeBanner";
import { AnnouncementBanner } from "./crm/AnnouncementBanner";

/**
 * App-top banner slot (mounted once in AppNew, above all routes). Hosts the calendar
 * reconnect banner (FT-CAL-2), the E11 Inc-2 read-only tenant-mode banner, and the
 * Feature A Inc-2 admin announcement banner — each self-gates and renders nothing
 * when its condition is absent, so this slot stays empty in the common case.
 *
 * Layout contract (fixes the Inc-2 overlap defect): the app chrome is viewport-
 * anchored — the header is `sticky top-0` and the desktop sidebar is `fixed
 * top-[50px]`. A banner in normal flow would be ignored by that anchored chrome
 * (and a static offset would desync on scroll), so the slot is a FIXED full-width
 * strip at top:0 and the shell is pushed DOWN by its live rendered height:
 *   - `--app-banner-h` (published here on <html>) drives `#root { padding-top }`
 *     — offsets the sticky header + in-flow content (and TzMismatchBanner);
 *   - the sticky header's `top` and the fixed sidebar's `top`/`height` also read
 *     the var (the desktop sidebar via an inline style on its #sky-sidebar
 *     wrapper), so the whole shell stays aligned at every scroll position.
 * A ResizeObserver keeps the var in sync as banners mount/unmount/resize (0→3,
 * D4 cap), so the offset tracks the stack height. Empty slot → 0px → no offset.
 *
 * Opacity contract: each child renders a SkyMessageBar whose intent background is
 * only ~10% opaque (`bg-info/10` etc.), so as a bare fixed strip page content
 * scrolls VISIBLY through it. The strip therefore carries an opaque neutral
 * surface background (`bg-surface`) so it fully occludes whatever scrolls under
 * it; the per-intent tint then layers over that solid surface and reads the same
 * as before. When the slot is empty its height is 0, so the opaque background
 * paints nothing (no stray bar) — matching the `.notification-toast` precedent.
 */
export function HeaderBanner() {
  const slotRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const el = slotRef.current;
    if (!el) return;
    const root = document.documentElement;
    const publishHeight = () => {
      // getBoundingClientRect().height is fractional (offsetHeight rounds to an
      // integer) — using the exact height avoids a sub-pixel seam/overlap at
      // fractional browser zoom.
      root.style.setProperty("--app-banner-h", `${el.getBoundingClientRect().height}px`);
    };
    publishHeight();
    // ResizeObserver is polyfilled everywhere this app runs; guard defensively.
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(publishHeight) : null;
    ro?.observe(el);
    return () => {
      ro?.disconnect();
      // Reset so unauthenticated / post-logout screens carry no phantom offset.
      root.style.setProperty("--app-banner-h", "0px");
    };
  }, []);

  return (
    <div
      ref={slotRef}
      data-testid="app-banner-slot"
      className="fixed top-0 right-0 left-0 z-[10002] bg-surface"
    >
      <ReadOnlyModeBanner />
      <CalendarReconnectBanner />
      <AnnouncementBanner />
    </div>
  );
}
